import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { getModalSwitch, getLoaderBtn } from '@app/store/actions';
import { AxiosResponse } from 'axios';
import { launchPadService, appLibraryService } from '@app/services';
import { useTypedTranslation } from '@app/hooks';
import { IWorkflowApplication, IActiveFilter, FilterEnum } from '@app/models';
import { useDebounce, onPressEnterModal } from '@app/helpers';
import { FormControl, CircularProgress } from '@material-ui/core';
import { IWorkflowStep } from '@app/pages/ssoManager/addEditApplication.enums';

interface workflowStepValues {
    key: string;
    value: string;
}

export default function AddNewApplicationToLibrary(props: any) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [addAppBtn, setAddAppBtn] = useState<boolean>(true);
    const [searchField, setSearchField] = useState<string>('');
    const [app, setApp] = useState<IWorkflowApplication[]>([]);
    const [secondStep, setSecondStep] = useState<boolean>(false);
    const [index, setIndex] = useState<number>();
    const [loading, setLoading] = useState<boolean>(true);

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [appName, setAppName] = useState<string>('');
    const [workflowSteps, setWorkflowSteps] = useState<IWorkflowStep[]>([]);
    const [newWorkflowValues, setNewWorkflowValues] = useState<[workflowStepValues]>([{} as workflowStepValues]);

    const debouncedSearchField = useDebounce(searchField, 100);

    useEffect(() => {
        handleGetAppInLibrary('DisplayName', 'asc');
    }, []);

    useEffect(() => {
        if (index !== undefined) {
            setAppName(app[index].DisplayName);
            setWorkflowSteps(JSON.parse(app[index].Workflow));
        }
    }, [index]);

    useEffect(() => {
        if (appName.length) {
            setAddAppBtn(false);
        } else {
            setAddAppBtn(true);
        }
    }, [appName]);

    useEffect(() => {
        handleSetValueFilter(searchField);
    }, [debouncedSearchField]);

    const handleGetAppInLibrary = (
        orderBy: string,
        asc: string,
        filter?: IActiveFilter[]
    ) => {
        launchPadService.getAppCatalog(orderBy, asc, filter)
            .then((response: AxiosResponse) => {
                setApp(response.data.value);
            })
            .catch(() => { })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSetValueFilter = (value: any) => {
        const filters: IActiveFilter[] = [
            {
                name: FilterEnum.contains,
                value: value,
                label: 'DisplayName',
                filterBy: 'DisplayName'
            }
        ];

        handleGetAppInLibrary('DisplayName', 'asc', filters);
    };

    const handleError = (errorResponse: any) => {
        if (errorResponse.data.error) {
            setErrorMessage(errorResponse.data.error.message);
        }
        else if (errorResponse.message) {
            setErrorMessage(errorResponse.message);
        }
        else {
            setErrorMessage(translation('GENERIC_ERROR'));
        }
    };

    const handleAddApp = (response: any) => {
        const results: IWorkflowApplication[] = response.config.data;

        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));

        if (results) {
            props.handleRefresh(true);
        }
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));

        if (index !== undefined) {
            let newVals: string = '{';

            for (let i: number = 1; i < newWorkflowValues.length; i++) {
                if (i > 1) {
                    newVals += ',';
                }
                newVals += `"${newWorkflowValues[i].key}":"${newWorkflowValues[i].value}"`;
            }

            newVals += '}';

            appLibraryService.addWorkflowCatalogApp(app[index].Id, app[index].CatalogId, appName, newVals, handleError, handleAddApp);
        }
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const getCategory = (category: string): string => {
        let parts = category.split('\\');
        if (parts.length > 1) {
            return parts[1];
        } else {
            return parts[0];
        }
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        if (name && value) {
            let index = -1;
            for (let i: number = 0; i < newWorkflowValues.length; i++) {
                if (newWorkflowValues[i].key === name) {
                    index = i;
                    break;
                }
            }

            if (index > -1) {
                newWorkflowValues.splice(index, 1);
            }

            let newValue: workflowStepValues = {} as workflowStepValues;
            newValue.key = name;
            newValue.value = value;

            newWorkflowValues.push(newValue);

            setNewWorkflowValues(newWorkflowValues);
        }
    };

    const isInputStep = (step: IWorkflowStep) => {
        if (step.step && step.step === 'input') {
            return true;
        }
        return false;
    };

    // the reason why the names look backwards in the following methods is because the original coders messed up the naming.
    // will tackle in the redesign
    const workflowAttrLabelValue = (rawVal: string | null) => {
        if (rawVal && rawVal === 'Password') {
            return translation('WORKFLOW_PLACEHOLDER_PASSWORD');
        }

        return translation('WORKFLOW_PLACEHOLDER_USERNAME');
    };

    const workflowAttrPlaceholderValue = (rawVal: string | null) => {
        if (rawVal && rawVal === 'Password') {
            return translation('WORKFLOW_LABEL_PASSWORD');
        }

        return translation('WORKFLOW_LABEL_USERNAME');
    };

    return (
        <>
            <div className='appLibraryList'  tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, addAppBtn)}>
                {!secondStep && <div className='appLibraryList__item appLibraryList__item--input-wrapper'>
                    <PasslyTextField
                        fullWidth
                        label=''
                        value={searchField}
                        onChange={(e) => setSearchField(e.target.value)}
                        id='search-input'
                        name='search'
                        placeholder={translation('SEARCH_FOR_APP')}
                    />
                </div>}
                {app !== undefined && !secondStep && app.map((item: IWorkflowApplication, index: number) => (
                    <div key={index} className='appLibraryList__item' onClick={() => { setSecondStep(true); setIndex(index); }}>
                        <div className='appLibraryList__item-cell'>
                            <img src={item.IconUrl} />
                            <div className='appLibraryList__item-cell--text appLibraryList__item-cell--display-name'>
                                {item.DisplayName}</div>
                        </div>
                        <div className='appLibraryList__item-cell appLibraryList__item-cell--text'>{item.Company}</div>
                        <div className='appLibraryList__item-cell appLibraryList__item-cell--text'>{getCategory(item.CategoryName)}</div>
                    </div>
                )) || app !== undefined && index !== undefined && secondStep &&
                    <div className='appLibraryList__details-row'>
                        <form className='appLibraryList__details-row-left'>
                            <div className='appLibraryList__details-row-card'>
                                <div>
                                    <img src={app[index].IconUrl} />
                                </div>
                            </div>
                            <FormControl>
                                <PasslyTextField
                                    label={'APP_NAME_LABEL'}
                                    autoComplete={translation('APP_NAME_LABEL')}
                                    placeholder={translation('APP_NAME_LABEL')}
                                    value={appName}
                                    onChange={(e) => setAppName(e.target.value)}
                                    id='displayName-input'
                                />
                            </FormControl>
                        </form>
                        <form className='appLibraryList__details-row-right'>
                            <h4>{translation('WORKFLOW_ATTRIBUTES')}</h4>
                            {workflowSteps.map((step: IWorkflowStep, index: number) =>
                                <>
                                    {isInputStep(step) &&
                                        <div key={index} className='modal__body--inner_row'>
                                            <FormControl>
                                                <PasslyTextField
                                                    label={workflowAttrLabelValue(step.value)}
                                                    name={step.elementval}
                                                    placeholder={workflowAttrPlaceholderValue(step.value)}
                                                    onChange={(e) => onFormChange(e)}
                                                    id={step.elementval}
                                                    type={step.type}
                                                />
                                            </FormControl>
                                        </div>

                                    }
                                </>
                            )
                            }
                        </form>
                    </div>
                }
                {
                    errorMessage &&
                    <div className='modal__error'>{errorMessage}</div>
                }
                {loading &&
                    <CircularProgress style={{ margin: 'auto' }} />
                }
            </div>
            <ModalActionsComponent
                primaryBtn={translation('ADD_APP')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                disabled={addAppBtn}
                id='addNewApp'
            >
            </ModalActionsComponent>
        </>
    );
}
